import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import Button from 'common/components/atoms/Button';
import Dropdown from 'common/components/atoms/Dropdown';
import Modal from 'common/components/organisms/Modal';
import ProgressBar from 'common/components/atoms/ProgressBar';

import { createAlarmParameter, getAvailableFeatures } from '../alarmParameters.service';
import {
  byFamily, featureLabel, featureOptionKey, featureOptionLabel, featureOptions, midSentence, tagTypeLabel
} from '../constants/alarmParameters.constants';
import {
  Actions,
  Consequence,
  ConsequenceLabel,
  ConsequenceRow,
  ConfirmNote,
  ConfirmQuestion,
  ConsequenceValue,
  Field,
  FieldLabel,
  Fields,
  Hint,
  Lede,
  Message,
  Mono
} from './CreateAlarmParameterModal.styled';

const T = 'adminDashboard.alarmParameters.create.';

// Nothing here is typed: the server derives the threshold keys and template names.
class CreateAlarmParameterModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tagType: null,
      feature: null,
      features: [],
      loadingFeatures: false,
      amplitude: null,
      confirming: false,
      saving: false,
      progress: 0,
      error: null
    };
  }

  componentWillUnmount() {
    this.stopProgress();
    if (this.doneTimer) clearTimeout(this.doneTimer);
  }

  /**
   * The create is one request that writes a row in each service; it takes a
   * second or two and gives no intermediate state. The bar advances on a
   * timer while the request is out, so the wait reads as progress rather
   * than a frozen button, and jumps to full when the server answers.
   */
  startProgress = () => {
    this.stopProgress();
    this.setState({ progress: 15 });
    this.progressTimer = setInterval(() => {
      this.setState(prev => ({ progress: Math.min(85, prev.progress + 10) }));
    }, 300);
  };

  stopProgress = () => {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  };

  selectTagType = (tagType) => {
    this.setState({
      tagType,
      feature: null,
      features: [],
      loadingFeatures: true,
      amplitude: null,
      confirming: false,
      error: null
    });

    // A slower answer for a tag type the user has already left is dropped.
    const isCurrent = () => this.state.tagType === tagType;
    getAvailableFeatures(tagType).then(
      (res) => {
        if (!isCurrent()) return;
        this.setState({
          features: (res && res.features) || [],
          loadingFeatures: false
        });
      },
      (error) => {
        if (!isCurrent()) return;
        this.setState({
          loadingFeatures: false,
          error: error && error.message
            ? error.message
            : this.props.t(`${T}loadError`, { defaultValue: 'Could not load features.' })
        });
      }
    );
  };

  /**
   * Amplitude comes off the row, not off the name. Today the two agree for
   * broadband, because the measurement column embeds it - rms_velocity. They
   * stop agreeing for narrowband, where shaft_speed_1x is two
   * feature_definition rows with one name and two amplitudes, so the pair is
   * the identity rather than the name alone.
   */
  amplitudes = () => {
    const seen = [];
    this.state.features.forEach((item) => {
      if (item.amplitude_type && seen.indexOf(item.amplitude_type) === -1) {
        seen.push(item.amplitude_type);
      }
    });

    return seen.sort();
  };

  available = () => this.state.features
    .filter(item => !item.enabled)
    .filter(item => !this.state.amplitude
      || item.amplitude_type === this.state.amplitude)
    .sort(byFamily);

  selected = () => this.state.features.find(
    item => featureOptionKey(item) === this.state.feature
  );

  save = () => {
    const choice = this.selected();
    if (!choice) return;

    this.setState({ saving: true, error: null });
    this.startProgress();

    createAlarmParameter(this.state.tagType, choice).then(
      () => {
        this.stopProgress();
        this.created = choice;
        this.setState({ progress: 100 });
        // Let the bar reach full before the dialog goes; the list owns the
        // message, because it also knows the tag type label.
        this.doneTimer = setTimeout(() => this.props.onCreated(choice), 500);
      },
      (error) => {
        this.stopProgress();
        // The server refuses a measure nothing can publish, and names the
        // feature and the record it looked at. That message is the useful one.
        this.setState({
          saving: false,
          progress: 0,
          confirming: false,
          error: error && error.message
            ? error.message
            : this.props.t(`${T}saveError`, { defaultValue: 'Could not add this alarm parameter.' })
        });
      }
    );
  };

  // Closing after a create still reloads the list; closing mid-request is ignored.
  close = () => {
    if (this.created) {
      clearTimeout(this.doneTimer);
      this.props.onCreated(this.created);
      return;
    }
    if (this.state.saving) return;

    this.props.onClose();
  };

  matchFeature = (options, query) => {
    const term = query.trim().toLowerCase();
    if (!term) return options;

    return options.filter(option => [option.text, option.value]
      .some(field => String(field || '').toLowerCase().indexOf(term) !== -1));
  };

  placeholder = (available) => {
    const { t } = this.props;
    if (!this.state.tagType) return t(`${T}chooseTagTypeFirst`, { defaultValue: 'Choose a tag type first' });
    if (this.state.loadingFeatures) return t(`${T}loading`, { defaultValue: 'Loading…' });
    if (!available.length) return t(`${T}nothingLeft`, { defaultValue: 'Nothing left to add' });

    return t(`${T}chooseFeature`, { defaultValue: 'Choose a feature' });
  };

  renderConsequence() {
    const choice = this.selected();
    if (!choice) return null;
    const { t } = this.props;

    return (
      <Consequence>
        <ConsequenceRow>
          <ConsequenceLabel>{t(`${T}reads`, { defaultValue: 'Reads' })}</ConsequenceLabel>
          <ConsequenceValue>
            <Mono>{choice.feature}</Mono>
            {' '}
            {t(`${T}readsOn`, {
              defaultValue: 'on every measurement from a {{tagType}} tag',
              tagType: midSentence(tagTypeLabel(this.state.tagType))
            })}
          </ConsequenceValue>
        </ConsequenceRow>
        <ConsequenceRow>
          <ConsequenceLabel>{t(`${T}customersGet`, { defaultValue: 'Customers get' })}</ConsequenceLabel>
          <ConsequenceValue>
            {t(`${T}customersGetText`, {
              defaultValue: 'A warning and a critical threshold for it, set per tag in'
            })}
            {' '}
            <strong>{t(`${T}chartsAlarms`, { defaultValue: 'Charts → Alarms' })}</strong>
          </ConsequenceValue>
        </ConsequenceRow>
      </Consequence>
    );
  }

  renderConfirm() {
    const choice = this.selected();
    const { saving, error } = this.state;
    const { t } = this.props;
    const tagType = midSentence(tagTypeLabel(this.state.tagType));

    return (
      <div>
        <ConfirmQuestion>
          {t(`${T}confirmQuestion`, {
            defaultValue: 'Create {{feature}} as an alarm parameter for {{tagType}} tags?',
            feature: featureOptionLabel(choice, midSentence),
            tagType
          })}
        </ConfirmQuestion>

        {this.renderConsequence()}

        <ConfirmNote>
          {t(`${T}confirmNote`, {
            defaultValue: 'Every account with a {{tagType}} tag can set this alarm as soon '
              + 'as it exists. It cannot be removed from this screen.',
            tagType
          })}
        </ConfirmNote>

        {saving && (
          <ProgressBar progress={this.state.progress} width="100%" height="6px" />
        )}

        <Actions>
          {error && <Message error>{error}</Message>}
          <Button
            text
            onClick={() => this.setState({ confirming: false })}
            disabled={saving}
          >
            {t(`${T}back`, { defaultValue: 'Back' })}
          </Button>
          <Button onClick={this.save} disabled={saving}>
            {saving
              ? t(`${T}creating`, { defaultValue: 'Creating…' })
              : t(`${T}confirmCreate`, { defaultValue: 'Yes, create it' })}
          </Button>
        </Actions>
      </div>
    );
  }

  render() {
    const { tagTypes, onClose, t } = this.props;
    const {
      tagType, feature, loadingFeatures, confirming, error
    } = this.state;
    const available = this.available();

    return (
      <Modal
        dialog
        title={t(`${T}title`, { defaultValue: 'Create alarm parameter' })}
        close={this.close}
        width="780px"
      >
        {confirming ? this.renderConfirm() : (
          <div>
            {!tagType && (
              <Lede>
                {t(`${T}lede`, {
                  defaultValue: 'Pick a tag type, then the measurement you want customers to '
                    + 'be able to alarm on. Only measurements the platform already '
                    + 'calculates are listed.'
                })}
              </Lede>
            )}

            <Fields>
              <Field>
                <FieldLabel>{t(`${T}tagType`, { defaultValue: 'Tag type' })}</FieldLabel>
                <Dropdown
                  options={tagTypes.map(type => ({
                    value: type,
                    text: tagTypeLabel(type)
                  }))}
                  value={tagType}
                  onChange={(e, data) => this.selectTagType(data.value)}
                  placeholder={t(`${T}chooseTagType`, { defaultValue: 'Choose a tag type' })}
                  aria-label={t(`${T}tagType`, { defaultValue: 'Tag type' })}
                />
              </Field>

              {this.amplitudes().length > 1 && (
                <Field>
                  <FieldLabel>{t(`${T}amplitude`, { defaultValue: 'Amplitude' })}</FieldLabel>
                  <Dropdown
                    options={[{ value: null, text: t(`${T}both`, { defaultValue: 'Both' }) }].concat(
                      this.amplitudes().map(amp => ({
                        value: amp,
                        text: featureLabel(amp)
                      }))
                    )}
                    value={this.state.amplitude}
                    onChange={(e, data) => this.setState({
                      amplitude: data.value,
                      feature: null,
                      error: null
                    })}
                    placeholder={t(`${T}both`, { defaultValue: 'Both' })}
                    aria-label={t(`${T}amplitude`, { defaultValue: 'Amplitude' })}
                  />
                </Field>
              )}

              <Field wide>
                <FieldLabel>{t(`${T}feature`, { defaultValue: 'Feature' })}</FieldLabel>
                <Dropdown
                  options={featureOptions(available)}
                  value={feature}
                  onChange={(e, data) => this.setState({
                    feature: data.value,
                    error: null
                  })}
                  placeholder={this.placeholder(available)}
                  disabled={!tagType || loadingFeatures || !available.length}
                  search={this.matchFeature}
                  noResultsMessage={t(`${T}noMatch`, { defaultValue: 'No feature matches that.' })}
                  aria-label={t(`${T}feature`, { defaultValue: 'Feature' })}
                />
              </Field>
            </Fields>

            {tagType && !loadingFeatures && !available.length && (
              <Hint>
                {t(`${T}allAdded`, {
                  defaultValue: 'Every feature a {{tagType}} tag can produce is already an alarm parameter.',
                  tagType: midSentence(tagTypeLabel(tagType))
                })}
              </Hint>
            )}

            {this.renderConsequence()}

            <Actions>
              {error && <Message error>{error}</Message>}
              <Button text onClick={onClose}>
                {t(`${T}cancel`, { defaultValue: 'Cancel' })}
              </Button>
              <Button
                onClick={() => this.setState({ confirming: true })}
                disabled={!feature}
              >
                {t(`${T}title`, { defaultValue: 'Create alarm parameter' })}
              </Button>
            </Actions>
          </div>
        )}
      </Modal>
    );
  }
}

CreateAlarmParameterModal.propTypes = {
  tagTypes: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

CreateAlarmParameterModal.defaultProps = {
  tagTypes: []
};

export default withTranslation()(CreateAlarmParameterModal);
